/**
 * The Templates page's decisions, apart from its markup: how templates are
 * grouped and labelled, what "Start from template" says to eve, and which
 * templates fit a document the library is missing.
 *
 * @module components/regulation/templates/templatesView
 */
import type { PolicyTemplate } from "../../../state/policyTemplates";

/** The firm's own templates first; then those from regulators and Amoreg. */
export const groupTemplates = (
  templates: ReadonlyArray<PolicyTemplate>,
  alreadyShown: ReadonlyArray<PolicyTemplate> = [],
): {
  readonly own: ReadonlyArray<PolicyTemplate>;
  readonly shared: ReadonlyArray<PolicyTemplate>;
} => {
  // Templates already listed above (those matching the document being drafted)
  // are not listed a second time in their group.
  const shown = new Set(alreadyShown.map((template) => template.id));
  const rest = templates.filter((template) => !shown.has(template.id));
  return {
    own: rest.filter((template) => template.organizationId !== null),
    shared: rest.filter((template) => template.organizationId === null),
  };
};

/** Who a template comes from, for its row. */
export const templateSourceLabel = (template: PolicyTemplate): string =>
  template.organizationId !== null
    ? "Your firm"
    : template.source === "regulator"
      ? (template.publisher ?? "Regulator")
      : (template.publisher ?? "Amoreg");

/** The format a person opens it in, or that there is no copy to open. */
export const templateFormatLabel = (template: PolicyTemplate): string =>
  template.licence === "link_only"
    ? "Link to original"
    : template.format === "docx"
      ? "Word"
      : template.format === "xlsx"
        ? "Excel"
        : "PDF";

/** "3 blanks", "1 blank", or null when none were found. */
export const blanksLabel = (template: PolicyTemplate): string | null =>
  template.placeholders.length === 0
    ? null
    : `${template.placeholders.length} blank${template.placeholders.length === 1 ? "" : "s"}`;

/** Only the firm's own templates are theirs to archive. */
export const canArchiveTemplate = (template: PolicyTemplate, canManage: boolean): boolean =>
  canManage && template.organizationId !== null;

/**
 * What "Start from template" puts in eve's composer. Names the template and
 * its id so eve fetches exactly this one, and loads the skill that fills,
 * tailors and files it.
 */
export const startFromTemplatePrompt = (
  template: PolicyTemplate,
  forDocument?: string | null,
): string => {
  const what = forDocument?.trim() || template.policyType?.trim() || template.title;
  return [
    `$policy-from-template Draft our ${what} from the template "${template.title}" (template id ${template.id}).`,
    "Fill every blank from our organisation's context and documents, ask me about anything you cannot find, and show me the draft before filing it.",
  ].join(" ");
};

/** Words that say what kind of document something is, not what it is about. */
const GENERIC_WORDS = new Set([
  "a",
  "an",
  "and",
  "for",
  "of",
  "the",
  "to",
  "on",
  "in",
  "policy",
  "policies",
  "procedure",
  "procedures",
  "template",
  "document",
  "framework",
  "statement",
  "manual",
  "missing",
  "no",
]);

const meaningfulWords = (text: string): Set<string> =>
  new Set(
    text
      .toLowerCase()
      .split(/[^a-z0-9]+/)
      .filter((word) => word.length > 1 && !GENERIC_WORDS.has(word)),
  );

/**
 * The templates that fit a document the library is missing, best first: the
 * share of the missing document's meaningful words a template's type and
 * title cover. "Conflicts of Interest Policy" matches a "Conflicts of
 * interest" template; nothing matches on "policy" alone.
 */
export const templatesForDocument = (
  templates: ReadonlyArray<PolicyTemplate>,
  documentName: string,
): ReadonlyArray<PolicyTemplate> => {
  const wanted = meaningfulWords(documentName);
  if (wanted.size === 0) return [];
  return templates
    .map((template) => {
      const offered = meaningfulWords(`${template.policyType ?? ""} ${template.title}`);
      const covered = [...wanted].filter((word) => offered.has(word)).length;
      return { template, score: covered / wanted.size, own: template.organizationId !== null };
    })
    .filter((entry) => entry.score >= 0.5)
    .sort((left, right) => right.score - left.score || Number(right.own) - Number(left.own))
    .map((entry) => entry.template);
};

/** The Templates page's search: `for`, the missing document a person came to draft. */
export function templatesSearch(search: Record<string, unknown>): { for?: string } {
  return typeof search["for"] === "string" && search["for"].trim()
    ? { for: search["for"].trim().slice(0, 300) }
    : {};
}
